// ─────────────────────────────────────────────
// Oike — loading the commercial tables without hiding failures.
// A table that could not be read is never shown as an empty list: its key goes
// to `loadErrors` and the screens say "no se pudo cargar" instead of "no hay".
// On a background refresh, a table that fails keeps what was already loaded.
// Shared by useAppData (browser) and scripts/test-data-load.mjs.
// ─────────────────────────────────────────────

/** The tables the app loads at start: data key → TABLE_IDS key and what the screens call it. */
export const APP_TABLES = [
  { key: 'accounts', table: 'accounts', label: 'empresas' },
  { key: 'stakeholders', table: 'stakeholders', label: 'contactos' },
  { key: 'opportunities', table: 'opportunities', label: 'oportunidades' },
  { key: 'actionPlan', table: 'actionPlan', label: 'plan de acción' },
  { key: 'outreach', table: 'outreach', label: 'actividad' },
  { key: 'solutions', table: 'solutions', label: 'servicios' },
  { key: 'events', table: 'events', label: 'eventos' },
  { key: 'clientPartners', table: 'clientPartners', label: 'socios' },
  { key: 'sources', table: 'sources', label: 'fuentes y enlaces' },
  { key: 'icp', table: 'icp', label: 'perfiles de cliente' },
  { key: 'users', table: 'users', label: 'usuarios' },
  { key: 'strategy', table: 'strategy', label: 'estrategia' },
  { key: 'proposals', table: 'proposals', label: 'propuestas' },
  { key: 'campaigns', table: 'campaigns', label: 'campañas' },
  { key: 'contentLab', table: 'contentLab', label: 'contenido' },
  { key: 'landings', table: 'landings', label: 'páginas publicadas' },
  { key: 'diagnosticIntakes', table: 'diagnosticIntake', label: 'solicitudes y cotizaciones' },
  { key: 'requestTopics', table: 'requestTopics', label: 'temas de solicitudes' },
];

export const LOAD_LABELS = Object.fromEntries(APP_TABLES.map(t => [t.key, t.label]));

/** The error thrown by AirtableAPI.fetchTable when a table cannot be read (never an empty list). */
export class TableLoadError extends Error {
  constructor(status, detail = '') {
    super(status === 403 ? 'Sin permiso para leer esta tabla'
      : status === 404 ? 'La tabla no existe en esta base'
      : status ? `Error ${status} al leer la tabla` : 'No se pudo conectar');
    this.name = 'TableLoadError';
    this.status = status || 0;
    this.detail = detail;
  }
}

/**
 * Merges the settled reads (Promise.allSettled, same order as `keys`) into data:
 * a read that failed keeps the previous rows of that table (or [] on the first
 * load) and is reported in `errors`, so a failure is never shown as "no records".
 */
export function settleTables(keys, settled, previous = {}) {
  const results = {};
  const errors = {};
  const missing = []; // 404: the table does not exist in this base (configuration, not a failed load)
  keys.forEach((k, i) => {
    const s = settled[i];
    if (s && s.status === 'fulfilled' && Array.isArray(s.value)) results[k] = s.value;
    else {
      results[k] = Array.isArray(previous[k]) ? previous[k] : [];
      const reason = s?.reason;
      errors[k] = reason?.name === 'TableLoadError' ? reason.message : 'No se pudo conectar';
      if (reason?.status === 404) missing.push(k);
    }
  });
  return { results, errors, missing };
}

/** The first load error among these data keys, or ''. */
export function loadErrorOf(data, keys) {
  const errs = data?.loadErrors || {};
  for (const k of [].concat(keys)) if (errs[k]) return errs[k];
  return '';
}

/**
 * The tables to warn about on every screen: the ones that failed to load. A table
 * that does not exist in this base (404) is not repeated on every page; the
 * screen that needs it says so (loadErrorOf still reports it).
 */
export function failedTableKeys(data) {
  const missing = new Set(data?.missingTables || []);
  return Object.keys(data?.loadErrors || {}).filter(k => !missing.has(k));
}

/** "empresas y contactos": the failed tables, as the screens name them. */
export function failedTablesText(loadErrors = {}, keys = Object.keys(loadErrors)) {
  const names = keys.map(k => LOAD_LABELS[k] || k);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}`;
}
